import type { FunctionComponent, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useIntersection } from "scmp-app/lib/hooks/intersection";
import { Typeform } from "scmp-app/lib/typeform";
import { useHomepageCSATTypeform } from "scmp-app/lib/typeform/hooks";

type ContextValue = {
  triggerElement?: ReactNode;
  typeformElement?: ReactNode;
};

type Props = {
  children: ReactNode;
};

const NewHomepageSurveyContext = createContext<ContextValue>({});

export const NewHomepageSurveyContextProvider: FunctionComponent<Props> = ({ children }) => {
  const { configKey, testing_group } = useHomepageCSATTypeform();
  const [isShowNewHomepageSurvey, setIsShowNewHomepageSurvey] = useState(false);
  const { captureIntersectionElement, intersection, setShouldDisable } = useIntersection({
    threshold: 1,
  });

  const typeformElement = useMemo(
    () =>
      isShowNewHomepageSurvey && <Typeform configKey={configKey} testing_group={testing_group} />,
    [configKey, isShowNewHomepageSurvey, testing_group],
  );

  const triggerElement = useMemo(
    () => <div id="new_homepage_typeform_trigger" ref={captureIntersectionElement} />,
    [captureIntersectionElement],
  );

  useEffect(() => {
    if (intersection?.isIntersecting !== true) return;
    setIsShowNewHomepageSurvey(true);
    setShouldDisable(true);
  }, [intersection, setShouldDisable]);

  return (
    <NewHomepageSurveyContext.Provider value={{ triggerElement, typeformElement }}>
      {children}
    </NewHomepageSurveyContext.Provider>
  );
};

export const useNewHomepageSurveyContext = () => useContext(NewHomepageSurveyContext);
